import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureException } from '@/lib/error-logger';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
import { tr } from '@/i18n';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Optional label to distinguish boundaries in logs. */
  scope?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const scope = this.props.scope ? `:${this.props.scope}` : '';
    console.error(`[ErrorBoundary${scope}]`, error, info);
    captureException(error, {
      kind: 'boundary',
      scope: this.props.scope,
      componentStack: info.componentStack ?? undefined,
    });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  private handleHome = () => {
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    // Class component: cannot use hooks. Use TR fallback labels — this fallback
    // only renders when the entire React tree has crashed, so language context
    // may itself be unavailable. TR is our canonical.
    const l = tr.errorBoundaryUi;
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-background px-4">
        <div className="max-w-md w-full text-center space-y-4 p-6 rounded-lg border border-border bg-card">
          <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-destructive" aria-hidden="true" />
          </div>
          <h1 className="text-xl font-semibold text-foreground">{l.title}</h1>
          <p className="text-sm text-muted-foreground">
            {l.description}
          </p>
          {this.state.error?.message && (
            <p className="text-xs text-muted-foreground font-mono break-all">
              {this.state.error.message}
            </p>
          )}
          <div className="flex gap-2 justify-center pt-2">
            <Button variant="outline" onClick={this.handleReset}>{l.retry}</Button>
            <Button onClick={this.handleHome}>{l.home}</Button>
          </div>
        </div>
      </div>
    );
  }
}
